'use client'

import ConferenceBooking from '@/components/common/ConferenceBooking'
import IconBadge from '@/components/common/IconBadge'
import ImageCarousel from '@/components/common/ImageCarousel'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Link } from '@/i18n/navigation'
import hotelTitle from '@/lib/helpers/hotelName'
import { Language } from '@/types/language.types'
import { MapPin, Scan, Users } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React from 'react'
import { HotelsWithConference, hotelsWithConference } from './action'
import Filter from '@/components/common/Filter'

interface ConferenceClientProps {
  conferenceHotels: HotelsWithConference
  locale: Language
  cities: { title: string; slug?: string | null; countrySlug?: string | null }[]
  countries: { title: string; slug?: string | null }[]
}

function ConferenceClient({ conferenceHotels, locale, cities, countries }: ConferenceClientProps) {
  const [filterCity, setFilterCity] = React.useState<string>('')
  const [filterCountry, setFilterCountry] = React.useState<string>('')
  const [cityOptions, setCityOptions] = React.useState(cities)

  const [items, setItems] = React.useState<HotelsWithConference>(conferenceHotels)
  const t = useTranslations()

  const onFilterCityChange = async (val: string) => {
    setFilterCity(val)

    setItems([])

    const result = await hotelsWithConference(locale, val)

    setItems(result)
  }

  const onFilterCountryChange = async (val: string) => {
    setFilterCountry(val)

    setItems([])

    setCityOptions(
      cities.filter((city) => {
        return city.countrySlug === val || val === ''
      }),
    )
    setFilterCity('')

    const result = await hotelsWithConference(locale, undefined, val)

    setItems(result)
  }

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        <h3>{t('conference_hotels')}</h3>
        <div className='flex gap-2'>
          <Filter
            value={filterCountry}
            items={countries.map((country) => ({
              title: country.title,
              value: country?.slug ?? '',
            }))}
            placeholder='Country'
            onValueChange={onFilterCountryChange}
          />
          <Filter
            value={filterCity}
            items={cityOptions.map((city) => ({ title: city.title, value: city?.slug ?? '' }))}
            onValueChange={onFilterCityChange}
            placeholder='City'
          />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {items.map((hotel) => {
          if (hotel.conferenceRooms?.length === 0) return null

          const largestByCapacity = hotel.conferenceRooms?.reduce((max, room) =>
            (room.capacity ?? 0) > (max.capacity ?? 0) ? room : max,
          )
          const smallestBySize = hotel.conferenceRooms?.reduce((min, room) =>
            (room.roomSize ?? 0) < (min.roomSize ?? 0) ? room : min,
          )

          const city = typeof hotel.city === 'object' ? hotel.city : null

          if (!city) return null

          return (
            <Card
              key={hotel.id}
              className='border bg-card rounded-xl md:rounded-3xl overflow-hidden max-w-lg pt-0 gap-0 relative'
            >
              {largestByCapacity?.capacity && (
                <span className='absolute text-sm top-4 left-4 z-10 bg-white rounded-full px-2 border border-white/50 backdrop-blur-md'>
                  {largestByCapacity?.capacity} {t('people')}
                </span>
              )}

              {hotel.images && hotel.images.length > 0 ? (
                <ImageCarousel media={hotel.images} />
              ) : (
                <div className='w-full aspect-16/9 bg-stone-300' />
              )}

              <div className='px-4 pt-4 h-full space-y-4 flex flex-col justify-between w-full'>
                <div className='space-y-4 pb-2'>
                  <div className='space-y-2'>
                    <h3 className='text-2xl'>{hotelTitle(hotel.title, hotel.hotelType)}</h3>
                    <div className='flex items-center space-x-1 text-muted-foreground'>
                      <MapPin className='size-5' strokeWidth={1.5} />
                      <p>
                        {city.title}, {typeof city.country === 'object' ? city.country.title : ''}
                      </p>
                    </div>
                  </div>

                  {largestByCapacity?.capacity && (
                    <div className='flex space-x-2 items-center'>
                      <IconBadge icon={Users} />
                      <p>{t('max_capacity', { capacity: largestByCapacity.capacity })}</p>
                    </div>
                  )}

                  {smallestBySize?.roomSize && (
                    <div className='flex space-x-2 items-center'>
                      <IconBadge icon={Scan} />
                      {smallestBySize?.roomSize === largestByCapacity?.roomSize ? (
                        <p>
                          {`${largestByCapacity?.roomSize}`}m<sup>2</sup>
                        </p>
                      ) : (
                        <p>
                          {`${smallestBySize?.roomSize} - ${largestByCapacity?.roomSize}`}m
                          <sup>2</sup>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className='w-full space-y-4 flex flex-col'>
                  <Link
                    href={{
                      pathname: '/hotels/[country]/[city]/[hotel]/conferences-meetings',
                      params: {
                        country: city.countrySlug as string,
                        city: city.slug as string,
                        hotel: hotel.slug as string,
                      },
                    }}
                    className='underline hover:opacity-50 transition-opacity font-medium'
                  >
                    {t('hotel_information')}
                  </Link>

                  <div className='w-full justify-end'>
                    {hotel.proposalesId ? (
                      <ConferenceBooking id={hotel.proposalesId} />
                    ) : (
                      <Button className='w-full h-12 rounded-full' variant={'secondary'}>
                        {t('see_options')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default ConferenceClient
